import { useEffect, useState } from 'react';
import { apiFetch, jsonBody } from '../../shared/api/httpClient';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { authErrorMessage } from './authShared';
import { deliverGoogleResult, exchangeGoogleCode } from './googleBridge';
import type { GoogleConfig } from './firebaseGoogle';

const opening = new Map<string, Promise<() => Promise<string>>>();
function openBridge(flow: string, locale: string) {
  let pending = opening.get(flow);
  if (!pending) {
    pending = Promise.all([
      apiFetch<GoogleConfig>('/api/auth/google/bridge/open', { method: 'POST', body: jsonBody({ flow }) }),
      import('./firebaseGoogle'),
    ]).then(([config, firebase]) => {
      if (!config.enabled || config.authOrigin !== window.location.origin) throw new Error('GOOGLE_INVALID_CREDENTIAL');
      const auth = firebase.prepareGoogle(config);
      auth.languageCode = locale;
      return () => firebase.googleCredential(auth);
    });
    opening.set(flow, pending);
  }
  return pending;
}
let callback: Promise<void> | undefined;
export function GoogleBridgePage() {
  const t = useI18nStore(s => s.t);
  const [params] = useState(() => new URLSearchParams(window.location.hash.slice(1)));
  const [credential, setCredential] = useState<(() => Promise<string>) | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const isReturn = window.location.pathname === '/auth/google/return';
  const flow = params.get('flow') || '';
  useEffect(() => {
    let cancelled = false;
    if (isReturn) {
      window.history.replaceState(null, '', window.location.pathname);
      callback ??= (async () => {
        const code = params.get('code') || '', state = params.get('state') || '';
        if (!/^[A-Za-z0-9_-]{43}$/.test(code) || !/^[A-Za-z0-9_-]{43}$/.test(state)) throw new Error('GOOGLE_INVALID_CREDENTIAL');
        if (await deliverGoogleResult(code, state)) { window.close(); return; }
        await exchangeGoogleCode(code, state);
        window.location.replace('/#/');
      })();
      void callback.catch(err => { if (!cancelled) setError(err); });
    } else if (/^[A-Za-z0-9_-]{43}$/.test(flow)) {
      void openBridge(flow, useI18nStore.getState().language).then(fn => { if (!cancelled) setCredential(() => fn); }).catch(err => { if (!cancelled) setError(err); });
    } else { setError(new Error('GOOGLE_INVALID_CREDENTIAL')); }
    return () => { cancelled = true; };
  }, [isReturn, flow, params]);
  async function login() {
    if (!credential || busy) return;
    setBusy(true); setError(null);
    try {
      const token = await credential();
      const result = await apiFetch<{ returnUrl: string }>('/api/auth/google/bridge/complete', { method: 'POST', body: jsonBody({ flow, credential: token }) });
      const target = new URL(result.returnUrl);
      if (target.protocol !== 'https:' || target.pathname !== '/auth/google/return') throw new Error('GOOGLE_INVALID_CREDENTIAL');
      window.location.replace(target.href);
    } catch (err) { setError(err); setBusy(false); }
  }
  return <main className="min-h-screen flex items-center justify-center bg-surface0 p-6">
    <section className="w-full max-w-sm rounded-2xl border border-border1 p-6 text-center">
      <h1 className="text-xl font-semibold mb-5">Sorteia</h1>
      {isReturn ? <p>{t('authSigningIn')}</p> : <button type="button" disabled={!credential || busy} onClick={() => void login()} className="w-full rounded-xl bg-white text-gray-900 font-semibold py-3 disabled:opacity-50">{t(busy ? 'authSigningIn' : 'googleContinue')}</button>}
      {error != null && <div role="alert" className="text-rose-400 mt-4"><p>{String((error as Error).message) === 'GOOGLE_EMAIL_EXISTS' ? t('googleEmailExists') : authErrorMessage(error, t, 'email', 'googleFailed')}</p><a className="underline" href="/#/auth.login">{t('googleRetry')}</a></div>}
    </section>
  </main>;
}
